import {
  createContext,
  Dispatch,
  useCallback,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import CenterFocusStrong from '@mui/icons-material/CenterFocusStrong'
import { OverlayState } from '@/hooks/useOverlay'
import useOverlayLocalStorage from '@/hooks/useOverlayLocalStorage'
import { DocumentChip } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import useRegisterRelevantID from '@/components/relevant-ids/useRegisterRelevantID'
import { useDocumentChipsContext } from './DocumentChipsProvider'

export type SelectedField = {
  document_row_id: string
  field: ProjectGridField
}

export type SelectedFieldContextValue = {
  selectedField: SelectedField | null
  selectedFieldChips: DocumentChip[]
  selectedFieldOverlay: OverlayState
  setSelectedField: Dispatch<SetStateAction<SelectedField | null>>
  isSelectedField: (
    fieldId: string | null,
    documentRowId: string | null,
  ) => boolean
}

const SelectedFieldContext = createContext<SelectedFieldContextValue | null>(
  null,
)

// Outside a SelectedFieldProvider there is nothing selected and no way to
// select something, so this is null instead of a fake object with working
// methods. Callers must check for null themselves.
export const useSelectedFieldContext = () => useContext(SelectedFieldContext)

type SelectedFieldProviderProps = {
  children: ReactNode
}

export default function SelectedFieldProvider({
  children,
}: SelectedFieldProviderProps) {
  const [selectedField, setSelectedField] = useState<SelectedField | null>(null)

  const selectedFieldOverlay = useOverlayLocalStorage({
    localStorageKey: 'selected-field-drawer-is-open',
  })

  const { getFieldChips } = useDocumentChipsContext()

  useRegisterRelevantID(
    selectedField
      ? {
          group: 'Document',
          type: 'Selected Field',
          label: selectedField.field.name,
          id: selectedField.field.id,
          icon: CenterFocusStrong,
        }
      : null,
  )

  // Expensive check. Memoizing since this rerenders _a lot_.
  const selectedFieldChips = useMemo(
    () =>
      selectedField
        ? getFieldChips(selectedField.field.id, selectedField.document_row_id)
        : [],
    [getFieldChips, selectedField],
  )

  const isSelectedField = useCallback(
    (fieldId: string | null, documentRowId: string | null): boolean =>
      selectedField?.document_row_id === documentRowId &&
      selectedField?.field.id === fieldId,
    [selectedField],
  )

  const value = useMemo(
    () => ({
      selectedField,
      selectedFieldChips,
      selectedFieldOverlay,
      setSelectedField,
      isSelectedField,
    }),
    [selectedField, selectedFieldChips, selectedFieldOverlay, isSelectedField],
  )

  return (
    <SelectedFieldContext.Provider value={value}>
      {children}
    </SelectedFieldContext.Provider>
  )
}
